import { useEffect, useState } from 'react';
import { Tags } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label
} from '@/shared/components/ui';
import { formatApiErrorMessage } from '../Utils';

interface AddCategoryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingCategories: string[];
  submitting?: boolean;
  onAdd: (category: string) => Promise<void> | void;
}

export const AddCategoryModal = ({
  open,
  onOpenChange,
  existingCategories,
  submitting,
  onAdd
}: AddCategoryModalProps) => {
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setName('');
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    if (!submitting) onOpenChange(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setError(null);

    const trimmed = name.trim();
    if (!trimmed) {
      setError('Category name is required');
      return;
    }

    const exists = existingCategories.some(
      cat => cat.toLowerCase() === trimmed.toLowerCase()
    );
    if (exists) {
      setError('That category already exists');
      return;
    }

    try {
      await onAdd(trimmed);
      onOpenChange(false);
    } catch (err: unknown) {
      setError(formatApiErrorMessage(err, 'Failed to create category'));
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={next => !submitting && onOpenChange(next)}
    >
      <DialogContent onClose={handleClose} className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5 text-[#087f8c]" />
            Add category
          </DialogTitle>
          <DialogDescription>
            Create a new inventory category. It will appear in the item category
            dropdowns.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="category-name">Category name</Label>
            <Input
              id="category-name"
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (error) setError(null);
              }}
              placeholder="e.g. Dairy & Eggs"
              autoFocus
              disabled={submitting}
              className="mt-1.5"
            />
            {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
          </div>

          <DialogFooter className="gap-2 pt-2 sm:gap-0">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={submitting}
              className="!bg-[#087f8c] text-white hover:!bg-[#066873]"
            >
              Create category
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
